import React from 'react';
import { Modal, Button, Table, Alert } from 'react-bootstrap';
import { SalesEntryData, useSalesEntry } from 'contexts/SalesEntryContext';

interface SubmitConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  salesEntryData: SalesEntryData;
  loading?: boolean;
}

const SubmitConfirmationModal: React.FC<SubmitConfirmationModalProps> = ({
  show,
  onHide,
  onConfirm,
  salesEntryData,
  loading = false
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const { state } = useSalesEntry()
  const totalDueAmt = state.summary.totalDueAmt
  // Calculate total payment from all payment methods
  const totalPayment = (salesEntryData.payment || []).reduce((sum, payment) => sum + payment.amount, 0);

  // Calculate net amount after all deductions
  const totalAmount = salesEntryData.summary.totalAmt || 0;
  const oldGoldAmount = salesEntryData.oldGold?.amount || 0;
  const exchangeAmount = salesEntryData.exchange?.totalAmount || 0;
  const rewardPointsAmount = salesEntryData.redemption?.amount || 0;
  const creditAmount = salesEntryData.customerDetails?.creditAmt || 0;
  let exchangeAmountToShow = state?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount;
  const total = exchangeAmountToShow;
  exchangeAmountToShow = (Math.floor(total * 100) / 100)?.toFixed(2);

  // Net amount customer needs to pay after all deductions
  const netAmountDue = totalAmount - oldGoldAmount - exchangeAmount - rewardPointsAmount - creditAmount;

  // Pending amount calculation
  const pendingAmount = Math.round(netAmountDue) - Math.round(totalPayment);
  // const pendingAmt = Math.max(0, netAmountDue - total - (totalPayment ?? 0.00));
  const safeNetAmountDue = Number(netAmountDue) || 0;
  const safeTotal = Number(total) || 0;
  const safeTotalPayment = Number(totalPayment) || 0;

  const pendingAmt = Math.max(0, Math.round(safeNetAmountDue - safeTotal - safeTotalPayment));


  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bx bx-check-circle text-warning me-2"></i>
          Confirm Sales Entry Submission
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Summary Information */}
        <Alert variant="info" className="mb-4">
          <div className="d-flex align-items-center">
            <i className="bx bx-info-circle fs-4 me-3"></i>
            <div>
              <h6 className="mb-1">Please review the details before submitting</h6>
              <small>Once submitted, this sales entry will be processed and cannot be easily modified.</small>
            </div>
          </div>
        </Alert>

        {/* Customer and Sales Person Details */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card border-0 bg-light">
              <div className="card-body p-3">
                <h6 className="card-title mb-2">
                  <i className="bx bx-user me-2"></i>Customer Details
                </h6>
                {salesEntryData.customer ? (
                  <div>
                    <div className="fw-semibold">{salesEntryData.customer.name}</div>
                    <div className="text-muted small">{salesEntryData.customer.phone}</div>
                    <div className="text-muted small">{salesEntryData.customer.email}</div>
                  </div>
                ) : (
                  <div className="text-muted">No customer selected</div>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card border-0 bg-light">
              <div className="card-body p-3">
                <h6 className="card-title mb-2">
                  <i className="bx bx-user-check me-2"></i>Sales Person
                </h6>
                {salesEntryData.salesPerson ? (
                  <div className="fw-semibold">{salesEntryData.salesPerson.name}</div>
                ) : (
                  <div className="text-muted">No sales person selected</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Items Summary */}
        <div className="mb-4">
          <h6 className="mb-3">
            <i className="bx bx-package me-2"></i>
            Items Summary ({salesEntryData.items.length} item{salesEntryData.items.length !== 1 ? 's' : ''})
          </h6>
          <div className="table-responsive">
            <Table bordered size="sm">
              <thead className="table-light">
                <tr>
                  <th>Style No.</th>
                  <th>Category</th>
                  <th>Net Wt</th>
                  <th>Metal Kt</th>
                  <th className="text-end">MRP</th>
                </tr>
              </thead>
              <tbody>
                {salesEntryData.items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.styleNo}</td>
                    <td>{item.categoryName}</td>
                    <td>{item.netWt.toFixed(3)}</td>
                    <td>{item.kt}</td>
                    <td className="text-end">{formatCurrency(item.mrp)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="card border-primary">
          <div className="card-header text-white">
            <h6 className="mb-0">
              <i className="bx bx-calculator me-2"></i>Financial Summary
            </h6>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <Table borderless size="sm">
                  <tbody>
                    <tr>
                      <td>Sub Amount:</td>
                      <td className="text-end fw-semibold">{formatCurrency(salesEntryData.summary.subAmt)}</td>
                    </tr>
                    <tr>
                      <td>Total Offers:</td>
                      <td className="text-end text-danger">-{formatCurrency(salesEntryData.summary.totalOffer)}</td>
                    </tr>
                    {/* Conditionally show GST rows */}
                    {salesEntryData?.customer?.isIgst ? (
                      <tr>
                        <td>IGST (3%):</td>
                        <td className="text-end">{formatCurrency(salesEntryData.summary.cgst + salesEntryData.summary.sgst)}</td>
                      </tr>
                    ) : (
                      <>
                        <tr>
                          <td>CGST (1.5%):</td>
                          <td className="text-end">{formatCurrency(salesEntryData.summary.cgst)}</td>
                        </tr>
                        <tr>
                          <td>SGST (1.5%):</td>
                          <td className="text-end">{formatCurrency(salesEntryData.summary.sgst)}</td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td>Total Redeem Amount</td>
                      <td className="text-end text-danger">
                        -{formatCurrency(
                          salesEntryData.items
                            ? Number(
                              salesEntryData.items
                                .reduce((sum: number, item: any) => sum + Number(item.totalRewardDiscount || 0), 0)
                                .toFixed(2)
                            )
                            : 0
                        )}
                      </td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-bold">Total Amount:</td>
                      <td className="text-end fw-bold fs-5 text-primary">
                        {formatCurrency(salesEntryData.summary.totalAmt)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="col-md-6">
                <Table borderless size="sm">
                  <tbody>
                    <tr>
                      <td>Old Gold Deduction:</td>
                      <td className="text-end text-success">+{formatCurrency(oldGoldAmount)}</td>
                    </tr>
                    <tr>
                      <td>Exchange Deduction:</td>
                      <td className="text-end text-success">-{formatCurrency(Number(exchangeAmountToShow) || 0)}</td>
                    </tr>
                    <tr>
                      <td>Credit Amount Used:</td>
                      <td className="text-end text-success">-{formatCurrency(creditAmount)}</td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-semibold">Net Amount Due:</td>
                      <td className="text-end fw-semibold text-primary">{formatCurrency(pendingAmt)}</td>
                    </tr>
                    <tr>
                      <td>Total Paid:</td>
                      <td className="text-end text-info">{formatCurrency(totalPayment)}</td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-bold">
                        {pendingAmt > 0 ? 'Pending Amount:' : pendingAmt < 0 ? 'Excess Payment:' : 'Fully Paid:'}
                      </td>
                      <td className={`text-end fw-bold fs-5 ${pendingAmt > 0 ? 'text-warning' : pendingAmt < 0 ? 'text-danger' : 'text-success'}`}>
                        {pendingAmt > 0 ? formatCurrency(pendingAmt) : pendingAmt < 0 ? formatCurrency(Math.abs(pendingAmt)) : formatCurrency(0)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        {salesEntryData.payment && salesEntryData.payment.length > 0 && (
          <div className="mt-4">
            <h6 className="mb-3">
              <i className="bx bx-credit-card me-2"></i>Payment Methods
            </h6>
            <div className="row">
              {salesEntryData.payment.map((payment, index) => (
                <div key={index} className="col-md-4 mb-2">
                  <div className="card border-0 bg-light">
                    <div className="card-body p-2 text-center">
                      <div className="fw-semibold">{payment.method}</div>
                      <div className="text-primary">{formatCurrency(payment.amount)}</div>
                      {payment.referenceNumber && (
                        <div className="text-muted small">Ref: {payment.referenceNumber}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warning for pending amount */}
        {totalDueAmt > 0 && (
          <Alert variant="warning" className="mt-3">
            <i className="bx bx-warning me-2"></i>
            <strong>Pending Payment:</strong> Customer still owes {formatCurrency(totalDueAmt)}.
            Ensure this amount is collected or record it as credit before proceeding.
          </Alert>
        )}

        {/* Warning for excess payment */}
        {totalDueAmt < 0 && (
          <Alert variant="info" className="mt-3">
            <i className="bx bx-info-circle me-2"></i>
            <strong>Excess Payment:</strong> Customer has paid {formatCurrency(Math.abs(totalDueAmt))} more than required.
            This excess amount will be recorded as advance credit for future purchases.
          </Alert>
        )}

        {/* Fully paid confirmation */}
        {pendingAmount === 0 && totalPayment > 0 && (
          <Alert variant="success" className="mt-3">
            <i className="bx bx-check-circle me-2"></i>
            <strong>Payment Complete:</strong> All amounts are fully settled.
            Net amount due ({formatCurrency(Math.round(netAmountDue))}) matches total payment ({formatCurrency(totalPayment)}).
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className='add-btn' onClick={onHide} disabled={loading}>
          <i className="bx bx-x me-1"></i>
          Cancel
        </Button>
        <Button

          onClick={onConfirm}
          disabled={loading}
          className="px-4 submit-btn"
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            <>
              <i className="bx bx-check me-1"></i>
              Confirm & Submit
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubmitConfirmationModal;